import type { LatLng } from './location-picker-map';

/**
 * Lectura de coordenadas escritas a mano (WGS84, grados decimales). Mismas
 * reglas que el value object `Coordenadas` del backend: número finito, latitud
 * en [-90, 90] y longitud en [-180, 180]. El punto es el separador decimal; la
 * coma decimal se rechaza con un mensaje claro porque «14,63» también podría ser
 * un par de coordenadas.
 */
export type LecturaCoordenada = { ok: true; valor: number } | { ok: false; error: string };

const NUMERO_DECIMAL = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;
const COMA_DECIMAL = /^[+-]?\d+,\d+$/;

export function leerCoordenada(texto: string, eje: 'lat' | 'lng'): LecturaCoordenada {
  const limpio = texto.trim().replace(/\s+/g, '');
  const nombre = eje === 'lat' ? 'latitud' : 'longitud';
  if (limpio === '') return { ok: false, error: `Ingrese la ${nombre}.` };
  if (COMA_DECIMAL.test(limpio)) {
    return { ok: false, error: 'Use punto decimal, por ejemplo 14.634915.' };
  }
  if (!NUMERO_DECIMAL.test(limpio)) {
    return { ok: false, error: `La ${nombre} debe ser un número en grados decimales.` };
  }
  const valor = Number(limpio);
  const limite = eje === 'lat' ? 90 : 180;
  if (!Number.isFinite(valor) || valor < -limite || valor > limite) {
    return { ok: false, error: `La ${nombre} debe estar entre -${limite} y ${limite}.` };
  }
  return { ok: true, valor };
}

const PAR = /^\s*\(?\s*([+-]?[\d.]+)\s*(?:[,;]\s*|\s+)([+-]?[\d.]+)\s*\)?\s*$/;

/**
 * Reconoce un par «latitud, longitud» pegado en un solo campo (el formato que
 * copia Google Maps: `14.634915, -90.506882`). Acepta coma, punto y coma o
 * espacio como separador. Devuelve `null` si el texto no es un par válido.
 */
export function leerParCoordenadas(texto: string): LatLng | null {
  const m = PAR.exec(texto);
  if (!m?.[1] || !m[2]) return null;
  const lat = leerCoordenada(m[1], 'lat');
  const lng = leerCoordenada(m[2], 'lng');
  return lat.ok && lng.ok ? { lat: lat.valor, lng: lng.valor } : null;
}

/**
 * Caja aproximada de Guatemala (con margen). Solo alimenta un aviso: el sistema
 * no prohíbe puntos fuera del país, pero el error típico al escribir a mano es
 * omitir el signo negativo de la longitud (oeste) y terminar en Asia.
 */
const GUATEMALA = { latMin: 13.5, latMax: 18, lngMin: -92.5, lngMax: -88 };

export function fueraDeGuatemala({ lat, lng }: LatLng): boolean {
  return (
    lat < GUATEMALA.latMin ||
    lat > GUATEMALA.latMax ||
    lng < GUATEMALA.lngMin ||
    lng > GUATEMALA.lngMax
  );
}

/** Texto de un eje con la precisión que se persiste (6 decimales, ~10 cm). */
export function formatearCoordenada(valor: number): string {
  return valor.toFixed(6);
}
